/**
 * Market Ordering Panel (B2 live admin control)
 *
 * Drag-sort + pin/feature toggles for live markets, in three views:
 *   Shuffle   — the global discovery order (displayOrder = row index on save)
 *   Category  — same global order, edited within one category at a time
 *   Dashboard — the app's dashboard shelf (dashboardRank = row index on save)
 *
 * The row order in this panel IS the order the app renders (backend leads its
 * ORDER BY with pinned DESC, display_order ASC — pinning floats a market to
 * the top of every surface, so toggling pin also moves the row up here to
 * stay WYSIWYG). Nothing is written until "Save order" — one bulk
 * transactional PUT via apiService.setMarketsOrdering.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/apiService';
import { MarketOrderingItem } from '../types/appConfig';
import './MarketOrderingPanel.css';

interface OrderingMarket {
  id: string;
  headline: string;
  category: string;
  image_url: string | null;
  pinned: boolean;
  featured: boolean;
  dashboard_rank: number | null;
}

type OrderingView = 'shuffle' | 'category' | 'dashboard';

const toOrderingMarket = (m: any): OrderingMarket => ({
  id: String(m.id),
  headline: m.headline || 'Untitled',
  category: m.category || 'uncategorized',
  image_url: m.image_url ?? null,
  pinned: m.pinned === true,
  featured: m.featured === true,
  dashboard_rank: typeof m.dashboard_rank === 'number' ? m.dashboard_rank : null,
});

/** Move one element inside a subsequence of `list` (identified by `subIds`,
 *  in list order) from subsequence index `from` to `to`, keeping every
 *  non-subsequence row in its slot. Shuffle view passes the full id set. */
function reorderWithin(
  list: OrderingMarket[],
  subIds: Set<string>,
  from: number,
  to: number
): OrderingMarket[] {
  const slots: number[] = [];
  list.forEach((m, i) => {
    if (subIds.has(m.id)) slots.push(i);
  });
  const sub = slots.map((i) => list[i]);
  const [moved] = sub.splice(from, 1);
  sub.splice(to, 0, moved);
  const next = [...list];
  slots.forEach((slot, k) => {
    next[slot] = sub[k];
  });
  return next;
}

const MarketOrderingPanel: React.FC = () => {
  // Canonical state: the FULL ordered list of live markets. Row index on
  // save = displayOrder.
  const [markets, setMarkets] = useState<OrderingMarket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<OrderingView>('shuffle');
  const [category, setCategory] = useState<string>('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setMsg(null);
    try {
      const result = await apiService.getAllMarkets({ status: 'live' });
      const rows = (result?.markets || []).map(toOrderingMarket);
      // Present in exactly the order the app resolves: pinned first, then
      // display_order, then the backend's own tiebreak order (stable sort).
      const sorted = rows
        .map((m: OrderingMarket, i: number) => ({ m, i, o: typeof (result.markets[i] as any).display_order === 'number' ? (result.markets[i] as any).display_order : 0 }))
        .sort((a: any, b: any) => {
          if (a.m.pinned !== b.m.pinned) return a.m.pinned ? -1 : 1;
          if (a.o !== b.o) return a.o - b.o;
          return a.i - b.i;
        })
        .map((x: any) => x.m);
      setMarkets(sorted);
      setDirty(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load markets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const categories = useMemo(
    () => Array.from(new Set(markets.map((m) => m.category))).sort(),
    [markets]
  );
  const activeCategory = category || categories[0] || '';

  const dashboardItems = useMemo(
    () =>
      markets
        .filter((m) => m.dashboard_rank !== null)
        .sort((a, b) => (a.dashboard_rank ?? 0) - (b.dashboard_rank ?? 0)),
    [markets]
  );
  const nonDashboardItems = useMemo(
    () => markets.filter((m) => m.dashboard_rank === null),
    [markets]
  );

  /** Rows visible in the current view, in view order. */
  const visible: OrderingMarket[] =
    view === 'shuffle'
      ? markets
      : view === 'category'
        ? markets.filter((m) => m.category === activeCategory)
        : dashboardItems;

  const mutate = (updater: (prev: OrderingMarket[]) => OrderingMarket[]) => {
    setMarkets(updater);
    setDirty(true);
    setMsg(null);
  };

  const moveVisible = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= visible.length || to >= visible.length) return;
    if (view === 'dashboard') {
      // Reorder the shelf, then re-rank 0..n-1.
      const shelf = [...dashboardItems];
      const [moved] = shelf.splice(from, 1);
      shelf.splice(to, 0, moved);
      const rankById = new Map(shelf.map((m, i) => [m.id, i]));
      mutate((prev) =>
        prev.map((m) => (rankById.has(m.id) ? { ...m, dashboard_rank: rankById.get(m.id)! } : m))
      );
      return;
    }
    const subIds = new Set(visible.map((m) => m.id));
    mutate((prev) => reorderWithin(prev, subIds, from, to));
  };

  const togglePin = (id: string) => {
    mutate((prev) => {
      const idx = prev.findIndex((m) => m.id === id);
      if (idx === -1) return prev;
      const m = { ...prev[idx], pinned: !prev[idx].pinned };
      const next = prev.filter((x) => x.id !== id);
      // Pinning floats the market to the top in the app — mirror it here.
      if (m.pinned) {
        next.unshift(m);
      } else {
        next.splice(idx, 0, m);
      }
      return next;
    });
  };

  const toggleFeatured = (id: string) => {
    mutate((prev) => prev.map((m) => (m.id === id ? { ...m, featured: !m.featured } : m)));
  };

  const addToDashboard = (id: string) => {
    const nextRank = dashboardItems.length;
    mutate((prev) => prev.map((m) => (m.id === id ? { ...m, dashboard_rank: nextRank } : m)));
  };

  const removeFromDashboard = (id: string) => {
    const shelf = dashboardItems.filter((m) => m.id !== id);
    const rankById = new Map(shelf.map((m, i) => [m.id, i]));
    mutate((prev) =>
      prev.map((m) =>
        m.id === id
          ? { ...m, dashboard_rank: null }
          : rankById.has(m.id)
            ? { ...m, dashboard_rank: rankById.get(m.id)! }
            : m
      )
    );
  };

  /** Max items per PUT — keeps each request well under the backend's bulk
   *  ordering limit even as the live market count grows. */
  const ORDERING_CHUNK_SIZE = 400;

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const items: MarketOrderingItem[] = markets.map((m, i) => ({
        id: m.id,
        displayOrder: i,
        pinned: m.pinned,
        featured: m.featured,
        dashboardRank: m.dashboard_rank,
      }));

      let updated = 0;
      const missing: string[] = [];
      for (let start = 0; start < items.length; start += ORDERING_CHUNK_SIZE) {
        const chunk = items.slice(start, start + ORDERING_CHUNK_SIZE);
        const res = await apiService.setMarketsOrdering(chunk);
        updated += res.updated;
        missing.push(...res.missing);
      }

      if (missing.length > 0) {
        setMsg(`Saved ${updated}; ${missing.length} market(s) no longer exist — list refreshed.`);
        await load();
      } else {
        setMsg(`Saved — ${updated} market(s) updated. Live in the app on next refresh.`);
        setDirty(false);
      }
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const renderRow = (m: OrderingMarket, idx: number, dashboard: boolean) => (
    <div
      key={m.id}
      className={`mop-row ${dragIdx === idx ? 'dragging' : ''} ${overIdx === idx ? 'drag-over' : ''}`}
      draggable
      onDragStart={() => setDragIdx(idx)}
      onDragEnd={() => {
        setDragIdx(null);
        setOverIdx(null);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (overIdx !== idx) setOverIdx(idx);
      }}
      onDrop={(e) => {
        e.preventDefault();
        if (dragIdx !== null) moveVisible(dragIdx, idx);
        setDragIdx(null);
        setOverIdx(null);
      }}
    >
      <span className="mop-drag-handle" title="Drag to reorder">⠿</span>
      <span className="mop-index">{idx + 1}</span>
      {m.image_url ? (
        <img className="mop-thumb" src={m.image_url} alt="" />
      ) : (
        <div className="mop-thumb mop-thumb-empty" />
      )}
      <div className="mop-info">
        <div className="mop-headline">{m.headline}</div>
        <div className="mop-category">{m.category}</div>
      </div>
      {!dashboard && m.dashboard_rank !== null && (
        <span className="mop-dash-badge" title="Position on the dashboard shelf">
          Dash #{m.dashboard_rank + 1}
        </span>
      )}
      <button
        type="button"
        className={`mop-toggle ${m.pinned ? 'active' : ''}`}
        title={m.pinned ? 'Unpin (stops floating to the top of every surface)' : 'Pin to the top of every surface'}
        onClick={() => togglePin(m.id)}
      >
        📌
      </button>
      <button
        type="button"
        className={`mop-toggle ${m.featured ? 'active' : ''}`}
        title={m.featured ? 'Remove featured flag' : 'Mark as featured'}
        onClick={() => toggleFeatured(m.id)}
      >
        {m.featured ? '⭐' : '☆'}
      </button>
      <div className="mop-updown">
        <button type="button" title="Move up" disabled={idx === 0} onClick={() => moveVisible(idx, idx - 1)}>
          ▲
        </button>
        <button
          type="button"
          title="Move down"
          disabled={idx === visible.length - 1}
          onClick={() => moveVisible(idx, idx + 1)}
        >
          ▼
        </button>
      </div>
      {dashboard && (
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => removeFromDashboard(m.id)}
        >
          ✕ Remove
        </button>
      )}
    </div>
  );

  return (
    <div className="mop-panel">
      <div className="mop-toolbar">
        <div className="mop-views">
          <button
            className={`tab ${view === 'shuffle' ? 'active' : ''}`}
            onClick={() => setView('shuffle')}
          >
            🔀 Shuffle order
          </button>
          <button
            className={`tab ${view === 'category' ? 'active' : ''}`}
            onClick={() => setView('category')}
          >
            🗂 Category order
          </button>
          <button
            className={`tab ${view === 'dashboard' ? 'active' : ''}`}
            onClick={() => setView('dashboard')}
          >
            📱 Dashboard shelf
          </button>
        </div>
        <div className="mop-actions">
          {msg && <span className="mop-msg">{msg}</span>}
          {dirty && !msg && <span className="mop-msg mop-dirty">Unsaved changes</span>}
          <button className="btn btn-secondary" onClick={() => void load()} disabled={saving}>
            Reload
          </button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save order'}
          </button>
        </div>
      </div>

      {view === 'category' && (
        <div className="mop-category-bar">
          <label htmlFor="mop-cat">Category</label>
          <select id="mop-cat" value={activeCategory} onChange={(e) => setCategory(e.target.value)}>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <span className="mop-hint">
            Reordering here changes the global order of these markets relative to each other —
            other categories keep their slots.
          </span>
        </div>
      )}

      {loading ? (
        <div className="mop-empty">Loading live markets…</div>
      ) : error ? (
        <div className="mop-empty mop-error">{error}</div>
      ) : visible.length === 0 && view !== 'dashboard' ? (
        <div className="mop-empty">No live markets.</div>
      ) : (
        <div className="mop-list">
          {visible.map((m, idx) => renderRow(m, idx, view === 'dashboard'))}
          {view === 'dashboard' && visible.length === 0 && (
            <div className="mop-empty">Dashboard shelf is empty — add markets below.</div>
          )}
        </div>
      )}

      {view === 'dashboard' && !loading && !error && (
        <div className="mop-dash-pool">
          <h3>Not on the dashboard</h3>
          {nonDashboardItems.length === 0 ? (
            <div className="mop-empty">Every live market is on the shelf.</div>
          ) : (
            <div className="mop-list">
              {nonDashboardItems.map((m) => (
                <div key={m.id} className="mop-row mop-row-static">
                  {m.image_url ? (
                    <img className="mop-thumb" src={m.image_url} alt="" />
                  ) : (
                    <div className="mop-thumb mop-thumb-empty" />
                  )}
                  <div className="mop-info">
                    <div className="mop-headline">{m.headline}</div>
                    <div className="mop-category">{m.category}</div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => addToDashboard(m.id)}
                  >
                    ＋ Add to dashboard
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MarketOrderingPanel;
